import { useState } from 'react';
import { ArrowRight, Eye, EyeOff, LoaderCircle, Lock, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider.jsx';
import { useNotification } from '../../components/Notifications/NotificationContext';
import AuthShell from './AuthShell.jsx';

export default function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);

  const { showError, showSuccess } = useNotification();
  const { login, resendVerification } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedEmail(null);

    try {
      await login(formData.email, formData.password);
      showSuccess('Login realizado com sucesso! Redirecionando...');
    } catch (err) {
      const data = err.response?.data;

      // Email ainda não confirmado: oferece reenviar o link
      if (data?.code === 'email_not_verified') {
        setUnverifiedEmail(data.email || formData.email);
        showError(data.error || 'Confirme seu email antes de entrar.');
      } else {
        showError(data?.error || err.message || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification(unverifiedEmail);
      showSuccess('Reenviamos o link de confirmação. Verifique seu email.');
    } catch (err) {
      showError('Não foi possível reenviar agora. Tente novamente em alguns minutos.');
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthShell
      active="login"
      title={
        <>
          Suas vendas <span className="sp-gradient-text">não pararam.</span>
        </>
      }
    >
      <h2 className="sp-card-title">Entrar</h2>
      <p className="sp-card-sub">
        Seus bots seguiram vendendo. Entre para ver os números.
      </p>

      {unverifiedEmail && (
        <div className="sp-verify-hint" style={{ marginTop: '1.1rem' }}>
          <Mail size={16} aria-hidden="true" />
          <span>
            Seu email <strong>{unverifiedEmail}</strong> ainda não foi confirmado.
            Verifique sua caixa de entrada e o <strong>SPAM</strong>.{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="sp-link"
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
            >
              {resending ? 'Reenviando...' : 'Reenviar link de confirmação'}
            </button>
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* EMAIL */}
        <div className="sp-field">
          <label htmlFor="email" className="sp-label">Email</label>
          <div className="sp-input-wrapper">
            <Mail className="sp-input-icon" size={16} aria-hidden="true" />
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoFocus
              autoComplete="email"
              placeholder="seu@email.com"
              disabled={loading}
              className="sp-input"
            />
          </div>
        </div>

        {/* SENHA */}
        <div className="sp-field">
          <label htmlFor="password" className="sp-label">Senha</label>
          <div className="sp-input-wrapper">
            <Lock className="sp-input-icon" size={16} aria-hidden="true" />
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
              autoComplete="current-password"
              placeholder="••••••••"
              disabled={loading}
              className="sp-input"
              style={{ paddingRight: '3rem' }}
            />
            <button
              type="button"
              className="sp-eye-toggle"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
            >
              {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
            </button>
          </div>

          <div className="sp-field-row">
            <span></span>
            <Link to="/forgot-password" className="sp-link">
              Esqueceu sua senha?
            </Link>
          </div>
        </div>

        {/* BOTÃO */}
        <button type="submit" className="sp-btn-primary" disabled={loading}>
          {loading ? (
            <>
              <LoaderCircle className="sp-spin" size={18} aria-hidden="true" />
              Entrando...
            </>
          ) : (
            <>
              Entrar
              <ArrowRight className="sp-btn-arrow" size={18} aria-hidden="true" />
            </>
          )}
        </button>

        <div className="sp-secure-note">
          <Lock size={13} aria-hidden="true" /> Conexão segura e criptografada
        </div>
      </form>

      <div className="sp-card-footer">
        Novo por aqui?{' '}
        <Link to="/register" className="sp-link">
          Crie sua conta grátis
        </Link>
      </div>
    </AuthShell>
  );
}
